var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bubble } from './Bubble';
import { useSafeId } from '../../core/hooks/useSafeId';
import { useToggle } from '../../core/hooks/useToggle';
var composeEventHandler = function (handler, eventHandler) {
    return function (event) {
        if (eventHandler) {
            eventHandler(event);
        }
        handler(event);
    };
};
/**
 * @tdr
 */
var TooltipComponent = function (_a) {
    var className = _a.className, content = _a.content, children = _a.children, placement = _a.placement, isOpen = _a.isOpen, onOpen = _a.onOpen, onClose = _a.onClose, _b = _a.disableFocusListener, disableFocusListener = _b === void 0 ? false : _b, _c = _a.disableHoverListener, disableHoverListener = _c === void 0 ? false : _c, _d = _a.disableTouchListener, disableTouchListener = _d === void 0 ? false : _d, _e = _a.leaveDelay, leaveDelay = _e === void 0 ? 200 : _e, _f = _a.enterDelay, enterDelay = _f === void 0 ? 300 : _f, _g = _a.enterTouchDelay, enterTouchDelay = _g === void 0 ? 500 : _g, _h = _a.leaveTouchDelay, leaveTouchDelay = _h === void 0 ? 1500 : _h, _j = _a.skiddingOffset, skiddingOffset = _j === void 0 ? 0 : _j, _k = _a.distanceOffset, distanceOffset = _k === void 0 ? 0 : _k, rest = __rest(_a, ["className", "content", "children", "placement", "isOpen", "onOpen", "onClose", "disableFocusListener", "disableHoverListener", "disableTouchListener", "leaveDelay", "enterDelay", "enterTouchDelay", "leaveTouchDelay", "skiddingOffset", "distanceOffset"]);
    var isControlled = useRef(isOpen !== undefined);
    var tooltipId = useSafeId();
    var _l = useState(), referenceElement = _l[0], setReferenceElement = _l[1];
    var ignoreNonTouchEvents = React.useRef(false);
    var ignoreNonBlurEvents = React.useRef(false);
    var closeTimer = useRef();
    var enterTimer = useRef();
    var leaveTimer = useRef();
    var touchTimer = useRef();
    var _m = useToggle(), isVisible = _m[0], show = _m[1], hide = _m[2];
    var clearAllTimers = function () {
        clearTimeout(leaveTimer.current);
        clearTimeout(enterTimer.current);
        clearTimeout(closeTimer.current);
        clearTimeout(touchTimer.current);
    };
    var handleOpen = useCallback(function (event) {
        if (event.type === 'focus') {
            ignoreNonBlurEvents.current = true;
        }
        if (onOpen) {
            onOpen(event);
        }
        show();
    }, [onOpen, show]);
    var handleClose = useCallback(function (event) {
        if (onClose) {
            onClose(event);
        }
        hide();
        setTimeout(function () {
            ignoreNonTouchEvents.current = false;
            ignoreNonBlurEvents.current = false;
        }, 0);
    }, [onClose, hide]);
    var handleEnter = function (event) {
        event.persist();
        if (ignoreNonTouchEvents.current && event.type !== 'touchstart') {
            return;
        }
        clearAllTimers();
        if (children.props.onMouseEnter) {
            children.props.onMouseEnter(event);
        }
        enterTimer.current = window.setTimeout(function () {
            handleOpen(event);
        }, enterDelay);
    };
    var handleLeave = function (event) {
        if (children.props.onMouseLeave) {
            children.props.onMouseLeave(event);
        }
        if (ignoreNonBlurEvents.current && event.type !== 'blur') {
            return;
        }
        clearAllTimers();
        leaveTimer.current = window.setTimeout(function () {
            handleClose(event);
        }, leaveDelay);
    };
    var handleTouchStart = function (event) {
        clearAllTimers();
        ignoreNonTouchEvents.current = true;
        touchTimer.current = window.setTimeout(function () {
            handleOpen(event);
        }, enterTouchDelay);
    };
    var handleTouchEnd = function (event) {
        clearAllTimers();
        leaveTimer.current = window.setTimeout(function () {
            handleClose(event);
        }, leaveTouchDelay);
    };
    useEffect(function () {
        var listener = function (event) {
            if (event.key === 'Esc' || event.key === 'Escape') {
                handleClose(event);
            }
        };
        document.addEventListener('keydown', listener);
        return function () {
            document.removeEventListener('keydown', listener);
        };
    }, [handleClose]);
    var childrenProps = __assign(__assign(__assign(__assign(__assign(__assign({}, rest), children.props), (disableHoverListener === false
        ? {
            onMouseOver: handleEnter,
            onMouseLeave: handleLeave,
        }
        : {})), (disableFocusListener === false
        ? {
            onFocus: composeEventHandler(handleOpen, children.props.onFocus),
            onBlur: composeEventHandler(handleClose, children.props.onBlur),
        }
        : {})), (disableTouchListener === false
        ? {
            onTouchStart: handleTouchStart,
            onTouchEnd: handleTouchEnd,
        }
        : {})), { 'aria-describedby': tooltipId, ref: setReferenceElement });
    return (React.createElement(React.Fragment, null,
        React.cloneElement(children, childrenProps),
        React.createElement(Bubble, __assign({ id: tooltipId, className: className, content: content, placement: placement, referenceElement: referenceElement, skiddingOffset: skiddingOffset, distanceOffset: distanceOffset, isOpen: (isControlled.current && isOpen) || (!isControlled.current && isVisible) || false }, (disableHoverListener
            ? {}
            : {
                onMouseEnter: handleEnter,
                onMouseLeave: handleLeave,
            })))));
};
export var Tooltip = React.memo(TooltipComponent);
//# sourceMappingURL=Tooltip.js.map